import type { WithT } from 'i18next';
import { flow } from 'lodash-es';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { Link } from '@mui/material';

import { AuthActions } from 'common/auth/actions';
import type { AuthState } from 'common/auth/types';
import withTranslate from 'common/i18n/i18n';
import { CIPrimaryButton } from 'common/viewcomp/forms/buttons';
import type { PageStatus } from 'common/viewcomp/general/ci_pagestatus';
import { withPageStatus } from 'common/viewcomp/general/ci_pagestatus';
import CIStepper from 'common/viewcomp/stepper/CIStepper';

import './create_pass.scss';
import UserUpdatePasswordForm from './user_update_password_form';

interface Props extends WithT {
    auth: AuthState;
    pageStatus: PageStatus;
    onBackToLogin?: () => void;
    activeStep: number;
    showStepper: boolean;
}

interface State {
    formSubmitted: boolean;
    isLoading: boolean;
}

const CreatePasswordFormComponent = (props: Props): ReactElement => {
    const { t, auth, pageStatus, onBackToLogin, activeStep, showStepper } = props;

    const [state, setState] = useState<State>({
        formSubmitted: false,
        isLoading: false,
    });

    const handleFormSubmitStatus = (): void => {
        pageStatus.dismiss();
        setState({
            formSubmitted: true,
            isLoading: true,
        });
    };

    useEffect((): void => {
        if (auth.error) {
            pageStatus.error(t(auth.error?.translationKey));
            setState({
                formSubmitted: false,
                isLoading: false,
            });
        }
    }, [auth.error, pageStatus, t]);

    return (
        <div className="activation_set_Password activation_home_container">
            <div className="upper">
                <div className="activation_header_text">{t('activation.create_password_step')}</div>
                {showStepper && <CIStepper activeStep={activeStep} />}
                <div className="user_id_instructions">
                    {t('activation.createA')} <strong>{t('profile.password')}</strong> {t('activation.forYourAccount')}
                </div>
            </div>
            {/* password and confirm password code */}
            <div id="passwordContainer" className="password_container">
                <div id="createPassView" className="createPassView">
                    <UserUpdatePasswordForm onSubmit={handleFormSubmitStatus} submitting={state.formSubmitted}>
                        {(disableButton: boolean): ReactElement => (
                            <CIPrimaryButton
                                type="submit"
                                id="activationCodeButton"
                                isLoading={state.isLoading || !auth.newPasswordRequested}
                                disabled={disableButton}
                            >
                                {t('activation.create_password_step')}
                            </CIPrimaryButton>
                        )}
                    </UserUpdatePasswordForm>
                    {onBackToLogin && !state.formSubmitted && (
                        <Link component="button" className="user-activation-back-to-login-link" onClick={onBackToLogin}>
                            {t('activation.back_to_login')}
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = ({ auth }: { auth: AuthState }) => ({ auth });

const mapDispatchToProps = { ...AuthActions };

const CreatePasswordForm = flow([withTranslate('user'), withPageStatus])(
    connect(mapStateToProps, mapDispatchToProps)(CreatePasswordFormComponent)
);

export default CreatePasswordForm;
